import { useEffect, useMemo, useState } from "react";

import type { WatchlistItem } from "@/services/api/types";

import {
  WATCHLIST_EXPORT_COLUMNS,
  buildWatchlistCsv,
  makeWatchlistExportFilename,
  normalizeExportColumnKeys,
  type WatchlistExportColumnKey,
} from "./watchlistExport";

interface WatchlistExportModalProps {
  open: boolean;
  watchlistName: string;
  items: WatchlistItem[];
  onClose: () => void;
}

export function WatchlistExportModal({ open, watchlistName, items, onClose }: WatchlistExportModalProps) {
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<Set<WatchlistExportColumnKey>>(new Set(["symbol"]));
  const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedColumnKeys(new Set(["symbol"]));
    setSelectedRowIds(new Set(items.map((item) => item.id)));
    setError(null);
  }, [open, items]);

  const selectedRows = useMemo(() => items.filter((item) => selectedRowIds.has(item.id)), [items, selectedRowIds]);
  const normalizedColumns = useMemo(
    () => normalizeExportColumnKeys(Array.from(selectedColumnKeys)),
    [selectedColumnKeys],
  );

  if (!open) {
    return null;
  }

  function toggleColumn(key: WatchlistExportColumnKey) {
    if (key === "symbol") {
      return;
    }
    setSelectedColumnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      next.add("symbol");
      return next;
    });
  }

  function toggleRow(id: number) {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function exportCsv() {
    if (!selectedRows.length) {
      setError("请至少选择一行");
      return;
    }
    if (!normalizedColumns.length) {
      setError("请至少选择一列");
      return;
    }
    const csv = buildWatchlistCsv(selectedRows, normalizedColumns);
    const filename = makeWatchlistExportFilename(watchlistName);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 px-3" onClick={onClose}>
      <div
        className="flex h-[min(78vh,720px)] w-[min(92vw,980px)] min-w-0 flex-col rounded-lg border border-[var(--border-strong)] bg-[#0f131b] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-slate-100">导出 Watchlist CSV</div>
            <div className="text-xs text-[var(--muted)]">{watchlistName}</div>
          </div>
          <button
            className="h-8 rounded border border-[var(--border)] bg-[#111823] px-3 text-xs text-slate-200 hover:bg-white/5"
            onClick={onClose}
          >
            关闭
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[220px_minmax(0,1fr)] gap-3 px-4 py-3 max-[900px]:grid-cols-1">
          <section className="rounded border border-[var(--border)] bg-[#0b1017] p-3">
            <div className="text-xs font-medium text-slate-100">导出列</div>
            <div className="mt-2 space-y-1">
              {WATCHLIST_EXPORT_COLUMNS.map((column) => {
                const checked = selectedColumnKeys.has(column.key);
                const locked = column.key === "symbol";
                return (
                  <label key={column.key} className="flex items-center gap-2 text-xs text-slate-200">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={locked}
                      onChange={() => toggleColumn(column.key)}
                      className="h-3.5 w-3.5 rounded border-[var(--border)] bg-[#0b1017]"
                    />
                    <span>{column.label}</span>
                    {locked ? <span className="text-[10px] text-[var(--muted)]">(必选)</span> : null}
                  </label>
                );
              })}
            </div>
          </section>

          <section className="flex min-h-0 flex-col rounded border border-[var(--border)] bg-[#0b1017]">
            <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
              <div className="text-xs text-slate-100">导出行 ({selectedRows.length}/{items.length})</div>
              <div className="flex items-center gap-2">
                <button
                  className="h-7 rounded border border-[var(--border)] bg-[#111823] px-2 text-xs text-slate-200 hover:bg-white/5"
                  onClick={() => setSelectedRowIds(new Set(items.map((item) => item.id)))}
                >
                  全选
                </button>
                <button
                  className="h-7 rounded border border-[var(--border)] bg-[#111823] px-2 text-xs text-slate-200 hover:bg-white/5"
                  onClick={() => setSelectedRowIds(new Set())}
                >
                  清空
                </button>
              </div>
            </div>
            <div className="terminal-scrollbar min-h-0 flex-1 overflow-auto">
              <table className="min-w-full text-xs">
                <thead className="sticky top-0 bg-[#111823] text-[var(--muted)]">
                  <tr>
                    <th className="w-12 px-2 py-2 text-left">选中</th>
                    <th className="px-2 py-2 text-left">代码</th>
                    <th className="px-2 py-2 text-left">名称</th>
                    <th className="px-2 py-2 text-right">最新价</th>
                    <th className="px-2 py-2 text-right">涨跌%</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const name = item.display_name || item.stock_name || item.name_cache || item.symbol;
                    const checked = selectedRowIds.has(item.id);
                    return (
                      <tr key={item.id} className="border-t border-[var(--border)] text-slate-200">
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(item.id)}
                            className="h-3.5 w-3.5 rounded border-[var(--border)] bg-[#0b1017]"
                          />
                        </td>
                        <td className="px-2 py-1.5">{item.symbol}</td>
                        <td className="px-2 py-1.5">{name}</td>
                        <td className="px-2 py-1.5 text-right">{item.close ?? "--"}</td>
                        <td className="px-2 py-1.5 text-right">{item.pct_chg != null ? `${item.pct_chg}%` : "--"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-between border-t border-[var(--border)] px-4 py-3">
          <div className="text-xs text-amber-200">{error || ""}</div>
          <div className="flex items-center gap-2">
            <button
              className="h-8 rounded border border-[var(--border)] bg-[#111823] px-3 text-xs text-slate-200 hover:bg-white/5"
              onClick={onClose}
            >
              取消
            </button>
            <button
              className="h-8 rounded border border-sky-400/40 bg-sky-500/20 px-3 text-xs text-sky-200 hover:bg-sky-500/30"
              onClick={exportCsv}
            >
              保存并下载
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
